import { Loader2, type LucideIcon } from 'lucide-react'
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'success' | 'secondary' | 'ghost' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  loading?: boolean
  icon?: LucideIcon
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading, icon: Icon, fullWidth, className, children, disabled, ...props },
  ref,
) {
  const variantStyles: Record<Variant, string> = {
    primary:
      'bg-[#053736] text-white shadow-sm hover:bg-[#074746] hover:shadow active:scale-[0.98] transition-all',
    success:
      'bg-emerald-600 text-white font-bold shadow-sm hover:bg-emerald-700 active:scale-[0.98] transition-all',
    secondary:
      'bg-white text-slate-800 border border-slate-200 shadow-sm hover:bg-slate-50 hover:border-slate-300 active:scale-[0.98] transition-all',
    ghost:
      'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:scale-[0.98] transition-all',
    danger:
      'bg-rose-600 text-white shadow-sm hover:bg-rose-700 active:scale-[0.98] transition-all',
  }

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl px-4 py-2 text-[13px]',
        'font-semibold tracking-[-0.1px] transition-all duration-150 cubic-bezier(0.4, 0, 0.2, 1)',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0',
        variantStyles[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        Icon && <Icon className="h-4 w-4" aria-hidden />
      )}
      {children}
    </button>
  )
})

interface FieldProps {
  label: string
  required?: boolean
  error?: string
  hint?: string
  children: ReactNode
}

export const Field = ({ label, required, error, hint, children }: FieldProps) => (
  <label className="block">
    <span className="mb-1.5 flex items-baseline gap-1.5 text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
      {label}
      {required ? (
        <span className="text-rose-600" aria-hidden>*</span>
      ) : (
        <span className="text-[10px] font-normal lowercase text-slate-400">(optional)</span>
      )}
    </span>
    {children}
    {hint && !error && <span className="mt-1 block text-[11.5px] text-slate-500">{hint}</span>}
    {error && <span className="mt-1 block text-[11.5px] font-medium text-rose-600">{error}</span>}
  </label>
)

const CONTROL = cn(
  'w-full min-h-[42px] rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13px]',
  'text-slate-900 placeholder:text-slate-400 transition-all duration-150 shadow-sm',
  'focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20',
  'disabled:bg-slate-50 disabled:text-slate-400',
)

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(CONTROL, className)} {...props} />
  },
)

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn(CONTROL, 'pr-8', className)} {...props}>
        {children}
      </select>
    )
  },
)

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} rows={3} className={cn(CONTROL, 'py-2.5', className)} {...props} />
  },
)

const BADGE_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  positive: {
    bg: '#ecfdf5',
    color: '#065f46',
    border: '#a7f3d0',
  },
  warning: {
    bg: '#fffbeb',
    color: '#92400e',
    border: '#fde68a',
  },
  danger: {
    bg: '#fef2f2',
    color: '#991b1b',
    border: '#fecaca',
  },
  info: {
    bg: '#eff6ff',
    color: '#1e40af',
    border: '#bfdbfe',
  },
  dark: {
    bg: '#0f172a',
    color: '#f8fafc',
    border: '#334155',
  },
  neutral: {
    bg: '#f1f5f9',
    color: '#334155',
    border: '#cbd5e1',
  },
}

export const Badge = ({
  label,
  semantic = 'neutral',
  icon: Icon,
}: {
  label: string
  semantic?: string
  icon?: LucideIcon
}) => {
  const style = BADGE_STYLES[semantic] ?? BADGE_STYLES.neutral
  return (
    <span
      style={{
        backgroundColor: style.bg,
        color: style.color,
        borderColor: style.border,
      }}
      className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold leading-tight shadow-sm"
    >
      {Icon && <Icon className="h-3 w-3" aria-hidden />}
      {label}
    </span>
  )
}

export const Card = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => (
  <div
    className={cn(
      'rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card transition-all duration-150 hover:shadow-card-hover',
      className,
    )}
  >
    {children}
  </div>
)

const TONE_COLORS: Record<string, { bar: string; iconBg: string; iconColor: string }> = {
  green: { bar: '#10b981', iconBg: '#ecfdf5', iconColor: '#059669' },
  amber: { bar: '#f59e0b', iconBg: '#fffbeb', iconColor: '#d97706' },
  red: { bar: '#ef4444', iconBg: '#fef2f2', iconColor: '#dc2626' },
  blue: { bar: '#3b82f6', iconBg: '#eff6ff', iconColor: '#2563eb' },
  purple: { bar: '#8b5cf6', iconBg: '#f5f3ff', iconColor: '#7c3aed' },
  teal: { bar: '#14b8a6', iconBg: '#f0fdfa', iconColor: '#0d9488' },
  dark: { bar: '#053736', iconBg: '#f1f5f9', iconColor: '#053736' },
}

export const StatCard = ({
  label,
  value,
  topColor,
  tone = 'green',
  icon: Icon,
  description,
  className,
}: {
  label: string
  value: string | number
  topColor?: string
  tone?: string
  icon?: LucideIcon
  description?: string
  className?: string
}) => {
  const meta = TONE_COLORS[tone] ?? TONE_COLORS.green
  const barColor = topColor || meta.bar

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card transition-all duration-150 hover:-translate-y-0.5 hover:shadow-card-hover',
        className,
      )}
    >
      <div
        style={{ backgroundColor: barColor }}
        className="absolute inset-x-0 top-0 h-1"
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && (
          <div
            style={{ backgroundColor: meta.iconBg, color: meta.iconColor }}
            className="flex h-7 w-7 items-center justify-center rounded-lg"
          >
            <Icon className="h-4 w-4" aria-hidden />
          </div>
        )}
      </div>
      <p className="mt-2 text-[26px] md:text-[28px] font-black tracking-tight text-slate-900 leading-none">
        {value}
      </p>
      {description && (
        <p className="mt-1.5 text-[11px] font-medium text-slate-500">{description}</p>
      )}
    </div>
  )
}

export const Skeleton = ({ className }: { className?: string }) => (
  <div className={cn('animate-pulse rounded-xl bg-slate-200/70', className)} aria-hidden />
)

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon
  title: string
  description?: string
  action?: ReactNode
}) => (
  <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 shadow-sm border border-emerald-100">
      <Icon className="h-6 w-6" aria-hidden />
    </div>
    <div>
      <p className="text-[15px] font-bold text-slate-900">{title}</p>
      {description && <p className="mt-1 text-[12.5px] text-slate-500">{description}</p>}
    </div>
    {action}
  </div>
)

export const ErrorState = ({
  title,
  message,
  onRetry,
}: {
  title: string
  message: string
  onRetry?: () => void
}) => (
  <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 shadow-sm">
    <p className="text-[13.5px] font-bold text-rose-800">{title}</p>
    <p className="mt-1 text-[12.5px] text-rose-700">{message}</p>
    {onRetry && (
      <Button variant="secondary" className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
)

export const Progress = ({ value, label }: { value: number; label?: string }) => (
  <div>
    {label && <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>}
    <div
      className="h-[7px] w-full overflow-hidden rounded-full bg-slate-200"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        style={{
          width: `${Math.min(100, Math.max(0, value))}%`,
        }}
        className="h-full rounded-full bg-emerald-600 transition-all duration-500 cubic-bezier(0.4, 0, 0.2, 1)"
      />
    </div>
  </div>
)

export const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h2 className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
    {children}
  </h2>
)

export const DetailRow = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-3 last:border-0 hover:bg-slate-50/80 transition-colors">
    <span className="text-[12.5px] font-semibold text-slate-500">{label}</span>
    <span className="text-right text-[13.5px] font-bold text-slate-900">{value || '—'}</span>
  </div>
)

export * from './TrcArrowLogo'
export * from './UserProfileModal'
export * from './UserManagementModal'
export * from './CameraCaptureModal'
